import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { TypographyProps } from "@mui/material/Typography";
import React, { HTMLAttributes, useRef, useState } from "react";
import { MuiBox, MuiIconButton, MuiTypography } from "..";
import { TOAST_AUTO_HIDE_DURATION } from "../../helpers/constants";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { copyToClipboard, isEmpty } from "../../helpers/utils";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";
import { MuiInlineToast } from "../toast/MuiInlineToast";
import { MuiCopyToClipBoardWrapper } from "./MuiCopyToClipBoard.style";

interface MuiCopyToClipBoardProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
  valueTypoVariant?: TypographyProps["variant"];
  valueClass: string;
  isValueWithEllipsis?: boolean;
  ariaLabelCopyToClipboard?: string;
  showCheckOnCopy?: boolean;
  showInlineToast?: boolean;
  toastText?: string;
}

export const MuiCopyToClipBoard: React.FC<MuiCopyToClipBoardProps> = ({
  value,
  valueTypoVariant = "h4Bold",
  valueClass,
  isValueWithEllipsis = false,
  ariaLabelCopyToClipboard,
  showCheckOnCopy = false,
  showInlineToast = false,
  toastText = "Copied",
}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const iconRef = useRef<HTMLButtonElement | null>(null);

  const handleContentCopy = async () => {
    await copyToClipboard(value);
    if (showCheckOnCopy) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), TOAST_AUTO_HIDE_DURATION);
    }

    if (showInlineToast) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), TOAST_AUTO_HIDE_DURATION);
    }
  };

  return (
    <MuiCopyToClipBoardWrapper>
      <MuiBox className="clipboard-value">
        {isValueWithEllipsis ? (
          <MuiEllipseLabel
            labelTypoVariant={valueTypoVariant}
            text={value}
            labelTypoClassName={`${valueClass} value-style`}
          />
        ) : (
          <MuiTypography
            component={"h4"}
            className={`${valueClass} value-style`}
            variant={valueTypoVariant}
          >
            {value}
          </MuiTypography>
        )}
      </MuiBox>
      <MuiIconButton
        className="copy-button"
        ref={iconRef}
        id={COMPONENT_IDS.COPY_TO_CLIPBOARD}
        data-testid={COMPONENT_IDS.COPY_TO_CLIPBOARD}
        aria-label={
          isEmpty(ariaLabelCopyToClipboard)
            ? "Copy to clipboard"
            : ariaLabelCopyToClipboard
        }
      >
        {showCheckOnCopy && isCopied ? (
          <CheckIcon data-testid="check-icon" className="check-icon" />
        ) : (
          <ContentCopyIcon
            data-testid="content-copy-icon"
            onClick={handleContentCopy}
          />
        )}
      </MuiIconButton>

      {showInlineToast && (
        <MuiInlineToast
          anchorEl={iconRef.current}
          open={showToast}
          onClose={() => setShowToast(false)}
          message={toastText}
          type="success"
        />
      )}
    </MuiCopyToClipBoardWrapper>
  );
};
